import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "@assets/icons/icons";
import { Button } from "./ui/button";
import { NAVIGATION_LINKS } from "@constants/navigation";
import { DesktopNavLinks } from "./DesktopNavLinks";
import { MobileNavLinks } from "./MobileNavLinks";

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {/* --- DESKTOP NAVBAR (Hidden on mobile) --- */}
      <header className="hidden lg:block sticky top-0 z-50 w-full bg-white/95 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
        <nav
          className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 py-3 sm:py-4 lg:px-8"
          aria-label="Global"
        >
          {/* Logo */}
          <div className="flex lg:flex-1">
            <Link to="/" className="flex items-center space-x-2 group">
              <img
                src="/logo.png"
                alt="Udyog Vriksh"
                className="h-10 w-auto"
                loading="lazy"
                decoding="sync"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <DesktopNavLinks links={NAVIGATION_LINKS} />

          {/* Desktop auth buttons */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-3">
            <Button
              variant="ghost"
              size="sm"
              className="font-medium text-gray-700 hover:text-primary hover:bg-gray-50"
              asChild
            >
              <Link to="/login">Sign In</Link>
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary-hover shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
              asChild
            >
              <Link to="/register">Get Started</Link>
            </Button>
          </div>
        </nav>
      </header>

      {/* --- MOBILE LAYOUT (Visible on mobile/tablet) --- */}
      <div
        ref={menuRef}
        className="lg:hidden fixed top-3 left-0 right-0 z-50 flex flex-col items-center md:items-end px-4 pointer-events-none"
      >
        {/* Pill Container */}
        <div className="w-full border border-gray-100 bg-white rounded-full shadow-xl px-4 py-2 flex justify-between items-center pointer-events-auto transition-all duration-300">
          {/* Logo Icon */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="UV"
              className="h-8 w-auto"
            // Using h-8 for a compact fit in the pill
            />
          </Link>

          {/* Hamburger Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-gray-800" /> : <Menu className="w-6 h-6 text-gray-800" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown (Animated) */}
        <div
          className={`
              w-full max-w-sm md:w-[320px] bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl mt-3 
              overflow-hidden transition-all duration-300 ease-in-out origin-top pointer-events-auto
              border border-gray-100
              ${mobileMenuOpen ? "max-h-[80vh] opacity-100 scale-100" : "max-h-0 opacity-0 scale-95"}
            `}
        >
          <div className="p-6 flex flex-col gap-4 border border-gray-100">
            {/* Nav Links */}
            <div className="flex flex-col gap-2">
              {NAVIGATION_LINKS.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-600 hover:text-primary font-medium text-sm py-2 border-b border-gray-100 last:border-0"
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Link
                to="/login"
                className="flex items-center justify-center py-2 rounded-xl bg-gray-100 text-gray-900 font-semibold text-sm hover:bg-gray-200 transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="flex items-center justify-center py-2 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover shadow-md transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
